/**
 * Flow graph geometry (DEC-188 decision 2, mock "Round 5 refined").
 *
 * A fixed 1000 x 600 canvas, scrolled horizontally inside its card on narrow
 * screens. Agent columns and rows come from WAVE_AGENTS, so a roster change
 * moves cards rather than breaking the drawing; the pseudo-nodes and the
 * hand-drawn curves keep the approved mock's coordinates.
 */
import type { GraphEdge } from './edgeModel'
import { PSEUDO } from './edgeModel'

export const CANVAS_W = 1000
export const CANVAS_H = 600
export const NODE_W = 150
export const NODE_H = 54
/** Vertical centre of the shared-signal band. */
export const SIGNAL_Y = 512
export const SIGNAL_BAND = { left: 300, top: SIGNAL_Y, width: 520, height: 40 }

const COL_X: Record<number, number> = { 1: 175, 2: 355, 3: 535, 4: 715 }
const ROW_Y = [40, 140, 240, 340]
const WAVE4_Y = 190

export interface NodePos {
  x: number
  y: number
}

export const PSEUDO_POS: Record<string, NodePos> = {
  [PSEUDO.intake]: { x: 0, y: 150 },
  [PSEUDO.planner]: { x: 0, y: 240 },
  [PSEUDO.history]: { x: 0, y: 330 },
  [PSEUDO.critic]: { x: 850, y: 140 },
  [PSEUDO.report]: { x: 850, y: 330 },
}

export function layoutNodes(waveAgents: Record<number, string[]>): Record<string, NodePos> {
  const pos: Record<string, NodePos> = { ...PSEUDO_POS }
  for (const n of [1, 2, 3, 4]) {
    const ids = waveAgents[n] ?? []
    ids.forEach((id, i) => {
      // Wave 4 is a single agent in the mock, centred between rows 2 and 3.
      const y = n === 4 && ids.length === 1 ? WAVE4_Y : (ROW_Y[i] ?? ROW_Y[ROW_Y.length - 1] + 100 * (i - ROW_Y.length + 1))
      pos[id] = { x: COL_X[n] ?? 715, y }
    })
  }
  return pos
}

/** Wave band rectangles behind the cards. */
export function waveBands(waveAgents: Record<number, string[]>): { num: number; x: number; w: number; h: number }[] {
  return [1, 2, 3, 4].map((num) => ({
    num,
    x: (COL_X[num] ?? 715) - 8,
    w: NODE_W + 16,
    h: num === 4 && (waveAgents[4] ?? []).length <= 1 ? 300 : 404,
  }))
}

type Pt = [number, number]

export interface EdgePath {
  d: string
  /** Arrival point, where the head dot sits. */
  head: Pt
  /** Where the label sits on a selected line. */
  mid: Pt
}

const curve = (a: Pt, b: Pt) => {
  const mx = (a[0] + b[0]) / 2
  return `M${a[0]},${a[1]} C${mx},${a[1]} ${mx},${b[1]} ${b[0]},${b[1]}`
}
const vline = (a: Pt, b: Pt) => `M${a[0]},${a[1]} L${b[0]},${b[1]}`

export function edgePath(e: GraphEdge, pos: Record<string, NodePos>): EdgePath | null {
  const p = (id: string) => pos[id]
  const R = (id: string): Pt => [p(id).x + NODE_W, p(id).y + NODE_H / 2]
  const L = (id: string): Pt => [p(id).x, p(id).y + NODE_H / 2]
  const T = (id: string): Pt => [p(id).x + NODE_W / 2, p(id).y]
  const B = (id: string): Pt => [p(id).x + NODE_W / 2, p(id).y + NODE_H]
  const mid = (a: Pt, b: Pt): Pt => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
  const fromSignal = e.from === PSEUDO.signal
  const toSignal = e.to === PSEUDO.signal
  if ((!fromSignal && !p(e.from)) || (!toSignal && !p(e.to))) return null

  const build = (d: string, start: Pt, head: Pt, m?: Pt): EdgePath => ({ d, head, mid: m ?? mid(start, head) })

  if (e.id === 'intake>planner') return build(vline(B(e.from), T(e.to)), B(e.from), T(e.to))
  if (e.id === 'planner>d10_idiomatic') {
    const s = R(e.from)
    const h = L(e.to)
    return build(`M${s[0]},${s[1]} C${s[0] + 10},470 ${h[0] - 15},470 ${h[0]},${h[1]}`, s, h, [440, 440])
  }
  if (e.from === PSEUDO.planner) return build(curve(R(e.from), L(e.to)), R(e.from), L(e.to))
  if (e.from === PSEUDO.history) {
    const s = R(e.from)
    const h = L(e.to)
    return build(`M${s[0]},${s[1]} C${s[0] + 180},470 ${h[0] - 65},${h[1] + 33} ${h[0]},${h[1]}`, s, h, [380, 360])
  }
  if (e.from === 'd1_complexity') {
    const h: Pt = [p(e.to).x, p(e.to).y + 20]
    return build(curve(R(e.from), h), R(e.from), h)
  }
  if (toSignal) {
    const s = B(e.from)
    const h: Pt = [s[0], SIGNAL_Y]
    return build(vline(s, h), s, h)
  }
  if (fromSignal) {
    const t = p(e.to)
    if (e.to === 'd7_performance') {
      const h = B(e.to)
      const x = h[0] - 10
      return build(`M${x},${SIGNAL_Y} L${x},${h[1]}`, [x, SIGNAL_Y], [x, h[1]])
    }
    if (e.to === 'd4_architecture') {
      const h: Pt = [t.x, t.y + 34]
      const x = t.x - 15
      return build(`M${x},${SIGNAL_Y} L${x},${h[1]} L${h[0]},${h[1]}`, [x, SIGNAL_Y], h, [x, (SIGNAL_Y + h[1]) / 2])
    }
    const h = L(e.to)
    const x = t.x - 6
    return build(`M${x},${SIGNAL_Y} L${x},${h[1]} L${h[0]},${h[1]}`, [x, SIGNAL_Y], h, [x, (SIGNAL_Y + h[1]) / 2])
  }
  if (e.id === 'critic>report') return build(vline(B(e.from), T(e.to)), B(e.from), T(e.to))
  if (e.id === 'd9_dead_code>report') {
    const s = R(e.from)
    const h = R(e.to)
    // Over the top and down the right edge, into the Report card's right side.
    return build(`M${s[0]},${s[1]} C${s[0] + 75},20 990,40 990,240 C990,${h[1] - 27} ${h[0] - 40},${h[1] - 17} ${h[0]},${h[1]}`, s, h, [990, 240])
  }
  if (e.to === PSEUDO.critic) return build(curve(R(e.from), L(e.to)), R(e.from), L(e.to))
  // Wave 3 vertical hand-offs (order and the service map).
  if (p(e.from).x === p(e.to).x) return build(vline(B(e.from), T(e.to)), B(e.from), T(e.to))
  return build(curve(R(e.from), L(e.to)), R(e.from), L(e.to))
}
